import { useEffect, useRef, useState } from "react";
import { ChevronDown, PanelLeft } from "lucide-react";
import { useIsBelowDesktop } from "../../../hooks/useIsPhone";
import { useBackToClose } from "./MobileSheet";

const findAside = () =>
  [...document.querySelectorAll("main .ds-page-aside")].find(
    (el) => getComputedStyle(el).display !== "none"
  ) || null;

const activeLabel = (aside) =>
  aside?.querySelector(".ds-page-nav-item-active")?.textContent.trim() || "Sections";

/**
 * Phone / tablet: a page's own `.ds-page-aside` becomes a slide-in drawer (CSS in index.css),
 * opened from a slim bar that names the current section. Desktop (>= 1280px) is untouched.
 */
export default function PageAsideDrawer() {
  const enabled = useIsBelowDesktop();
  const [aside, setAside] = useState(null);
  const [label, setLabel] = useState("Sections");
  const [open, setOpen] = useState(false);
  const closeThenRef = useRef(null);
  closeThenRef.current = useBackToClose(open, () => setOpen(false));
  const active = enabled && !!aside;

  useEffect(() => {
    const main = document.querySelector("main");
    if (!enabled || !main) {
      setAside(null);
      return undefined;
    }
    let frame = 0;
    const scan = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setAside(findAside()));
    };
    scan();
    // ponytail: child-list only (no attribute watching) to stay cheap on very large pages;
    // an aside shown purely by a class toggle is picked up on the next DOM insert.
    const mo = new MutationObserver(scan);
    mo.observe(main, { childList: true, subtree: true });
    return () => {
      mo.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [enabled]);

  useEffect(() => {
    if (!aside) return undefined;
    const update = () => setLabel(activeLabel(aside));
    update();
    const mo = new MutationObserver(update);
    mo.observe(aside, { subtree: true, childList: true, attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, [aside]);

  useEffect(() => {
    if (!active) setOpen(false);
    const root = document.documentElement;
    root.classList.toggle("pace-aside-drawer", active);
    root.classList.toggle("pace-aside-open", active && open);
    return () => root.classList.remove("pace-aside-drawer", "pace-aside-open");
  }, [active, open]);

  // Picking a section: close first, then replay the tap so the page's own handler runs
  // after the drawer's history entry is gone (a navigation is never undone by Back).
  useEffect(() => {
    if (!open || !aside) return undefined;
    aside.querySelector(".ds-page-nav-item-active, button")?.focus({ preventScroll: true });
    const onClick = (e) => {
      const target = e.target.closest?.("button, a");
      if (!target || !aside.contains(target) || target.hasAttribute("aria-expanded")) return;
      e.preventDefault();
      e.stopPropagation();
      closeThenRef.current(() => target.click());
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [open, aside]);

  if (!active) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        aria-label={`Sections: ${label}`}
        className="fixed z-30 flex h-11 items-center gap-2 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-4 text-left text-sm font-semibold text-[hsl(var(--ds-fg))] transition-colors active:bg-[hsl(var(--ds-primary)/0.08)] [-webkit-tap-highlight-color:transparent]"
        style={{
          top: "calc(var(--app-header-height, 5rem) + var(--admin-secondary-nav-height, 0px))",
          left: "var(--admin-left-sidebar-width, 0px)",
          right: "var(--admin-right-sidebar-width, 0px)",
        }}
      >
        <PanelLeft className="h-4 w-4 shrink-0 text-[hsl(var(--ds-primary))]" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-[hsl(var(--ds-muted-fg))]" aria-hidden="true" />
      </button>
      {open && (
        <button
          type="button"
          aria-label="Close sections"
          tabIndex={-1}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[65] cursor-default bg-black/40 animate-in fade-in duration-200 motion-reduce:animate-none"
        />
      )}
    </>
  );
}
